import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from './common/Modal';
import { adminApi, type Client, type CurrencyDto } from '../api/admin';
import {
  CreatePaymentReturnDraftRequest,
  IncomingProviderReturn,
  Payment,
  PaymentReturnDocument,
  paymentReturnsApi,
  paymentsApi,
} from '../api/payments';
import { getClientOptionLabel } from '../utils/clientDisplay';
import { notifyError, notifyErrorMessage, notifySuccess } from '../utils/notify';
import EnglishDatePicker from './common/EnglishDatePicker';

type PaymentReturnsPanelProps = {
  selectedClientId?: string;
  canManage: boolean;
  canApprove: boolean;
  initialOriginalPaymentId?: string;
  onInitialOriginalPaymentConsumed: () => void;
};

const eligibleStatuses = new Set(['SENT', 'COMPLETED', 'FAILED']);
const activeReturnStatuses = new Set(['DRAFT', 'PENDING', 'CREDITED', 'SUSPENSE']);
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function formatMoney(amount: number, currencyCode: string): string {
  const code = (currencyCode || '').trim().toUpperCase();
  if (code && !UUID_REGEX.test(code)) {
    try {
      return amount.toLocaleString('en-US', { style: 'currency', currency: code, minimumFractionDigits: 2 });
    } catch {
      /* fall through */
    }
  }
  return code ? `${amount.toFixed(2)} ${code}` : amount.toFixed(2);
}

function emptyForm(): CreatePaymentReturnDraftRequest {
  return {
    originalOutgoingPaymentId: '',
    receivedAmount: 0,
    valueDate: new Date().toISOString().slice(0, 10),
    providerReference: '',
    comment: '',
  };
}

function normalizePaymentId(value: string): string {
  return value.trim().toLowerCase().replace(/[{}]/g, '');
}

function paymentDisplayName(payment?: Payment): string {
  if (!payment) return '—';
  return (
    payment.beneficiary?.name?.trim() ||
    payment.clientDisplayName?.trim() ||
    payment.recipientClientEmail?.trim() ||
    payment.reference?.trim() ||
    'Outgoing payment'
  );
}

export default function PaymentReturnsPanel({
  selectedClientId,
  canManage,
  canApprove,
  initialOriginalPaymentId,
  onInitialOriginalPaymentConsumed,
}: PaymentReturnsPanelProps) {
  const [returns, setReturns] = useState<IncomingProviderReturn[]>([]);
  const [paymentsWithActiveReturn, setPaymentsWithActiveReturn] = useState<Set<string>>(() => new Set());
  const [payments, setPayments] = useState<Payment[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<IncomingProviderReturn | null>(null);
  const [documents, setDocuments] = useState<PaymentReturnDocument[]>([]);
  const [editing, setEditing] = useState<IncomingProviderReturn | null>(null);
  const [form, setForm] = useState<CreatePaymentReturnDraftRequest>(emptyForm);
  const [formClientId, setFormClientId] = useState('');
  const [paymentIdQuery, setPaymentIdQuery] = useState('');

  const currencyCodeByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const currency of currencies) {
      if (!currency?.id || !currency?.code) continue;
      map.set(currency.id.toUpperCase(), currency.code.toUpperCase());
      map.set(currency.code.toUpperCase(), currency.code.toUpperCase());
    }
    return map;
  }, [currencies]);

  const resolveCurrencyLabel = useCallback(
    (rawCurrencyId: string | null | undefined) => {
      const raw = (rawCurrencyId || '').trim();
      if (!raw) return '';
      const byKey = currencyCodeByKey.get(raw.toUpperCase());
      if (byKey) return byKey;
      return UUID_REGEX.test(raw) ? '' : raw.toUpperCase();
    },
    [currencyCodeByKey],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [returnsResponse, occupancyResponse, paymentsResponse, clientsResponse, currenciesResponse] = await Promise.all([
        paymentReturnsApi.list({
          clientId: selectedClientId,
          status: status || undefined,
          limit: 500,
        }),
        paymentReturnsApi.list({
          clientId: selectedClientId,
          limit: 2000,
        }),
        paymentsApi.getAll(),
        adminApi.getClients().catch(() => ({ data: [] as Client[] })),
        adminApi.getCurrencies().catch(() => ({ data: [] as CurrencyDto[] })),
      ]);
      setReturns(returnsResponse.data || []);
      setPaymentsWithActiveReturn(new Set(
        (occupancyResponse.data || [])
          .filter((paymentReturn) => activeReturnStatuses.has((paymentReturn.status || '').toUpperCase()))
          .map((paymentReturn) => paymentReturn.originalOutgoingPaymentId)
          .filter((id): id is string => !!id),
      ));
      setPayments(paymentsResponse.data || []);
      setClients(clientsResponse.data || []);
      setCurrencies(currenciesResponse.data || []);
    } catch (error: unknown) {
      notifyError(error, 'Failed to load payment returns');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const allEligiblePayments = useMemo(
    () =>
      payments.filter((payment) => {
        if (!eligibleStatuses.has((payment.status || '').toUpperCase())) return false;
        if (paymentsWithActiveReturn.has(payment.id)) return false;
        return true;
      }),
    [payments, paymentsWithActiveReturn],
  );

  const eligiblePayments = useMemo(() => {
    const query = normalizePaymentId(paymentIdQuery);
    return allEligiblePayments.filter((payment) => {
      if (formClientId && payment.clientId !== formClientId) return false;
      if (!query) return true;
      return normalizePaymentId(payment.id).includes(query);
    });
  }, [allEligiblePayments, formClientId, paymentIdQuery]);

  const paymentById = useMemo(() => new Map(payments.map((payment) => [payment.id, payment])), [payments]);

  const paymentSelectOptions = useMemo(() => {
    const options = [...eligiblePayments];
    const selectedId = form.originalOutgoingPaymentId || editing?.originalOutgoingPaymentId;
    if (!selectedId) return options;
    if (options.some((payment) => payment.id === selectedId)) return options;
    const selected = paymentById.get(selectedId);
    if (selected) options.unshift(selected);
    return options;
  }, [eligiblePayments, form.originalOutgoingPaymentId, editing?.originalOutgoingPaymentId, paymentById]);

  const selectedPayment = form.originalOutgoingPaymentId ? paymentById.get(form.originalOutgoingPaymentId) : undefined;
  const receivedAmountIsValid = !!selectedPayment && form.receivedAmount > 0 && form.receivedAmount <= selectedPayment.amount;

  const resolveReturnCurrency = useCallback(
    (paymentReturn: IncomingProviderReturn) => {
      const fromDictionary = resolveCurrencyLabel(paymentReturn.currencyId);
      if (fromDictionary) return fromDictionary;
      const original = paymentReturn.originalOutgoingPaymentId
        ? paymentById.get(paymentReturn.originalOutgoingPaymentId)
        : undefined;
      return resolveCurrencyLabel(original?.currency) || original?.currency || '';
    },
    [paymentById, resolveCurrencyLabel],
  );

  const originalPaymentLabel = useCallback(
    (paymentReturn: IncomingProviderReturn) => {
      const payment = paymentReturn.originalOutgoingPaymentId
        ? paymentById.get(paymentReturn.originalOutgoingPaymentId)
        : undefined;
      if (!payment) return '—';
      const name = paymentDisplayName(payment);
      const currency = resolveCurrencyLabel(payment.currency) || payment.currency || resolveReturnCurrency(paymentReturn);
      const amount = formatMoney(payment.amount, currency);
      return payment.reference?.trim() ? `${name} · ${payment.reference.trim()} · ${amount}` : `${name} · ${amount}`;
    },
    [paymentById, resolveCurrencyLabel, resolveReturnCurrency],
  );

  const applyPaymentSelection = useCallback((payment: Payment | undefined) => {
    if (!payment) {
      setForm((current) => ({ ...current, originalOutgoingPaymentId: '', receivedAmount: 0 }));
      return;
    }
    setFormClientId(payment.clientId || '');
    setForm((current) => ({
      ...current,
      originalOutgoingPaymentId: payment.id,
      receivedAmount: payment.amount,
    }));
    setPaymentIdQuery(payment.id);
  }, []);

  useEffect(() => {
    if (!initialOriginalPaymentId) return;
    const payment = paymentById.get(initialOriginalPaymentId);
    if (!payment) return;
    if (paymentsWithActiveReturn.has(payment.id)) {
      notifyErrorMessage('A payment return already exists for this outgoing payment.');
      onInitialOriginalPaymentConsumed();
      return;
    }
    setEditing(null);
    setForm({
      ...emptyForm(),
      originalOutgoingPaymentId: payment.id,
      receivedAmount: payment.amount,
    });
    setFormClientId(payment.clientId || selectedClientId || '');
    setPaymentIdQuery(payment.id);
    setCreateOpen(true);
    onInitialOriginalPaymentConsumed();
  }, [initialOriginalPaymentId, onInitialOriginalPaymentConsumed, paymentById, paymentsWithActiveReturn, selectedClientId]);

  useEffect(() => {
    if (!detail) {
      setDocuments([]);
      return;
    }
    void paymentReturnsApi.listDocuments(detail.id)
      .then((response) => setDocuments(response.data || []))
      .catch((error: unknown) => {
        notifyError(error, 'Failed to load payment return documents');
        setDocuments([]);
      });
  }, [detail]);

  const openCreate = (payment?: Payment) => {
    setEditing(null);
    if (payment) {
      if (paymentsWithActiveReturn.has(payment.id)) {
        notifyErrorMessage('A payment return already exists for this outgoing payment.');
        return;
      }
      setForm({
        ...emptyForm(),
        originalOutgoingPaymentId: payment.id,
        receivedAmount: payment.amount,
      });
      setFormClientId(payment.clientId || selectedClientId || '');
      setPaymentIdQuery(payment.id);
    } else {
      setForm(emptyForm());
      setFormClientId(selectedClientId || '');
      setPaymentIdQuery('');
    }
    setCreateOpen(true);
  };

  const openEdit = (paymentReturn: IncomingProviderReturn) => {
    setEditing(paymentReturn);
    setForm({
      originalOutgoingPaymentId: paymentReturn.originalOutgoingPaymentId || '',
      receivedAmount: paymentReturn.receivedAmount,
      valueDate: paymentReturn.valueDate.slice(0, 10),
      providerReference: paymentReturn.providerReference || '',
      comment: paymentReturn.backofficeComment || '',
    });
    setFormClientId(paymentReturn.clientId || selectedClientId || '');
    setPaymentIdQuery(paymentReturn.originalOutgoingPaymentId || '');
    setCreateOpen(true);
  };

  const findPaymentByIdQuery = () => {
    const query = normalizePaymentId(paymentIdQuery);
    if (!query) {
      notifyErrorMessage('Enter a payment ID to search.');
      return;
    }
    if (!formClientId) {
      notifyErrorMessage('Select a client first.');
      return;
    }

    const scoped = allEligiblePayments.filter((payment) => payment.clientId === formClientId);
    const exact = scoped.find((payment) => normalizePaymentId(payment.id) === query);
    const match = exact || scoped.find((payment) => normalizePaymentId(payment.id).includes(query));
    if (!match) {
      const alreadyReturned = payments.some(
        (payment) =>
          payment.clientId === formClientId &&
          paymentsWithActiveReturn.has(payment.id) &&
          (normalizePaymentId(payment.id) === query || normalizePaymentId(payment.id).includes(query)),
      );
      notifyErrorMessage(
        alreadyReturned
          ? 'A payment return already exists for this outgoing payment.'
          : 'No eligible outgoing payment found for this client and Payment ID.',
      );
      return;
    }
    applyPaymentSelection(match);
  };

  const onClientChange = (clientId: string) => {
    setFormClientId(clientId);
    setPaymentIdQuery('');
    setForm((current) => ({
      ...current,
      originalOutgoingPaymentId: '',
      receivedAmount: 0,
    }));
  };

  const saveDraft = async () => {
    if (!receivedAmountIsValid) return;
    try {
      setSaving(true);
      if (editing) {
        await paymentReturnsApi.updateDraft(editing.id, {
          receivedAmount: form.receivedAmount,
          valueDate: form.valueDate,
          providerReference: form.providerReference || undefined,
          comment: form.comment || undefined,
        });
        notifySuccess('Payment return draft updated');
      } else {
        await paymentReturnsApi.createDraft({
          ...form,
          providerReference: form.providerReference || undefined,
          comment: form.comment || undefined,
          idempotencyKey: crypto.randomUUID(),
        });
        notifySuccess('Payment return draft created');
      }
      setCreateOpen(false);
      await load();
    } catch (error: unknown) {
      notifyError(error, 'Failed to save payment return draft');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (action: () => Promise<unknown>, success: string, failure: string) => {
    try {
      setSaving(true);
      await action();
      notifySuccess(success);
      setDetail(null);
      await load();
    } catch (error: unknown) {
      notifyError(error, failure);
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = (paymentReturn: IncomingProviderReturn) => {
    const reason = window.prompt(`Delete draft return for ${paymentReturn.originalOutgoingPaymentId || paymentReturn.id}? Enter reason:`);
    if (!reason?.trim()) return;
    void runAction(
      () => paymentReturnsApi.deleteDraft(paymentReturn.id, reason.trim()),
      'Payment return draft deleted',
      'Failed to delete payment return draft',
    );
  };

  const rejectDraft = (paymentReturn: IncomingProviderReturn) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason?.trim()) return;
    void runAction(
      () => paymentReturnsApi.reject(paymentReturn.id, reason.trim()),
      'Payment return rejected',
      'Failed to reject payment return',
    );
  };

  const overrideFee = (paymentReturn: IncomingProviderReturn) => {
    const amount = window.prompt('Enter final return fee amount:', String(paymentReturn.returnFeeAmount));
    if (amount === null) return;
    const reason = window.prompt('Enter override reason:');
    if (!reason?.trim() || !Number.isFinite(Number(amount))) return;
    void runAction(
      () => paymentReturnsApi.overrideFee(paymentReturn.id, Number(amount), reason.trim()),
      'Return fee overridden',
      'Failed to override return fee',
    );
  };

  const uploadDocument = async (file: File | undefined) => {
    if (!detail || !file) return;
    try {
      setSaving(true);
      await paymentReturnsApi.uploadDocument(detail.id, file);
      const response = await paymentReturnsApi.listDocuments(detail.id);
      setDocuments(response.data || []);
      notifySuccess('Payment return document uploaded');
    } catch (error: unknown) {
      notifyError(error, 'Failed to upload payment return document');
    } finally {
      setSaving(false);
    }
  };

  const downloadDocument = async (attachment: PaymentReturnDocument) => {
    if (!detail) return;
    try {
      const response = await paymentReturnsApi.downloadDocument(detail.id, attachment.id);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      notifyError(error, 'Failed to download payment return document');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="PENDING">Pending</option>
            <option value="CREDITED">Credited</option>
            <option value="SUSPENSE">Suspense</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <span className="text-sm text-gray-500">{returns.length} return{returns.length === 1 ? '' : 's'}</span>
        </div>
        {canManage && (
          <button type="button" onClick={() => openCreate()} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
            Create return
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Value date', 'Original payment', 'Currency', 'Received', 'Return fee', 'Credited', 'Difference', 'Status', 'Actions'].map((title) => (
                <th key={title} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{title}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {!loading && returns.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">No payment returns found</td></tr>
            )}
            {returns.map((paymentReturn) => {
              const currency = resolveReturnCurrency(paymentReturn);
              return (
              <tr key={paymentReturn.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{new Date(paymentReturn.valueDate).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{originalPaymentLabel(paymentReturn)}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{currency || '—'}</td>
                <td className="px-4 py-3 text-right text-sm">{formatMoney(paymentReturn.receivedAmount, currency)}</td>
                <td className="px-4 py-3 text-right text-sm">{formatMoney(paymentReturn.returnFeeAmount, currency)}</td>
                <td className="px-4 py-3 text-right text-sm font-semibold">{formatMoney(paymentReturn.creditedAmount, currency)}</td>
                <td className="px-4 py-3 text-right text-sm">{formatMoney(paymentReturn.differenceAmount, currency)}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{paymentReturn.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    <button type="button" onClick={() => setDetail(paymentReturn)} className="rounded bg-indigo-600 px-2 py-1 text-xs text-white">Details</button>
                    {canManage && paymentReturn.status === 'DRAFT' && (
                      <>
                        <button type="button" onClick={() => openEdit(paymentReturn)} className="rounded bg-amber-500 px-2 py-1 text-xs text-white">Edit</button>
                        {canApprove && (
                          <button type="button" onClick={() => void runAction(() => paymentReturnsApi.confirm(paymentReturn.id), 'Payment return credited', 'Failed to confirm payment return')} className="rounded bg-emerald-600 px-2 py-1 text-xs text-white">Confirm</button>
                        )}
                        <button type="button" onClick={() => overrideFee(paymentReturn)} className="rounded bg-slate-600 px-2 py-1 text-xs text-white">Fee</button>
                        <button type="button" onClick={() => rejectDraft(paymentReturn)} className="rounded bg-orange-600 px-2 py-1 text-xs text-white">Reject</button>
                        <button type="button" onClick={() => deleteDraft(paymentReturn)} className="rounded bg-red-600 px-2 py-1 text-xs text-white">Delete</button>
                      </>
                    )}
                    {canApprove && paymentReturn.status === 'SUSPENSE' && (
                      <button type="button" onClick={() => void runAction(() => paymentReturnsApi.creditFromSuspense(paymentReturn.id), 'Payment return credited', 'Failed to credit from suspense')} className="rounded bg-emerald-600 px-2 py-1 text-xs text-white">Credit</button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title={editing ? 'Edit payment return draft' : 'Create payment return'} size="lg">
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Client
            <select
              disabled={!!editing}
              value={formClientId}
              onChange={(event) => onClientChange(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
            >
              <option value="">Select client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {getClientOptionLabel(client)}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Payment ID
            <div className="mt-1 flex gap-2">
              <input
                disabled={!!editing || !formClientId}
                value={paymentIdQuery}
                onChange={(event) => setPaymentIdQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    findPaymentByIdQuery();
                  }
                }}
                placeholder={formClientId ? 'Paste or type Payment ID' : 'Select client first'}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm disabled:bg-gray-50"
              />
              <button
                type="button"
                disabled={!!editing || !formClientId || !paymentIdQuery.trim()}
                onClick={findPaymentByIdQuery}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Find
              </button>
            </div>
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Original payment
            <select
              disabled={!!editing || !formClientId}
              value={form.originalOutgoingPaymentId}
              onChange={(event) => {
                const payment = paymentById.get(event.target.value);
                applyPaymentSelection(payment);
              }}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
            >
              <option value="">
                {!formClientId
                  ? 'Select client first'
                  : eligiblePayments.length === 0
                    ? 'No eligible payments for this client'
                    : 'Select eligible outgoing payment'}
              </option>
              {paymentSelectOptions.map((payment) => (
                <option key={payment.id} value={payment.id}>
                  {paymentDisplayName(payment)} · {formatMoney(payment.amount, resolveCurrencyLabel(payment.currency) || payment.currency)}
                </option>
              ))}
            </select>
          </label>
          {selectedPayment && (
            <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
              <div>Provider: {selectedPayment.providerName || selectedPayment.clientAccountProviderName || '—'}</div>
              <div>Recipient: {selectedPayment.beneficiary?.name || selectedPayment.recipientClientEmail || '—'}</div>
              <div>Original amount: {formatMoney(selectedPayment.amount, resolveCurrencyLabel(selectedPayment.currency) || selectedPayment.currency)}</div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">Received amount
              <input type="number" min="0.01" max={selectedPayment?.amount} step="0.01" value={form.receivedAmount || ''} onChange={(event) => setForm((current) => ({ ...current, receivedAmount: Number(event.target.value) }))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm font-medium text-gray-700">Value date
              <EnglishDatePicker value={form.valueDate} onChange={(value) => setForm((current) => ({ ...current, valueDate: value }))} className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </label>
          </div>
          {selectedPayment && <p className="text-sm text-gray-600">Difference amount: {formatMoney(selectedPayment.amount - form.receivedAmount, resolveCurrencyLabel(selectedPayment.currency) || selectedPayment.currency)}</p>}
          <label className="block text-sm font-medium text-gray-700">Provider reference
            <input value={form.providerReference || ''} onChange={(event) => setForm((current) => ({ ...current, providerReference: event.target.value }))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm font-medium text-gray-700">Comment
            <textarea value={form.comment || ''} onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" rows={3} />
          </label>
          {!receivedAmountIsValid && form.originalOutgoingPaymentId && <p className="text-sm text-red-600">Received amount cannot exceed the original payment amount.</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCreateOpen(false)} className="rounded-lg px-4 py-2 text-sm text-gray-600">Cancel</button>
            <button type="button" disabled={!formClientId || !receivedAmountIsValid || saving} onClick={() => void saveDraft()} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save draft'}</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!detail} onClose={() => setDetail(null)} title="Payment return details" size="lg">
        {detail && (() => {
          const currency = resolveReturnCurrency(detail);
          return (
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          {[
            ['Original payment', originalPaymentLabel(detail)],
            ['Currency', currency || '—'],
            ['Status', detail.status],
            ['Received amount', formatMoney(detail.receivedAmount, currency)],
            ['Return fee', `${formatMoney(detail.returnFeeAmount, currency)}${detail.isOverridden ? ' · overridden' : ''}`],
            ['Credited to client', formatMoney(detail.creditedAmount, currency)],
            ['Difference amount', formatMoney(detail.differenceAmount, currency)],
            ['Value date', new Date(detail.valueDate).toLocaleDateString()],
            ['Provider reference', detail.providerReference || '—'],
            ['Fee status', detail.feeDeferred ? 'Deferred' : detail.feeSkipReason || (detail.feeApplied ? 'Applied' : 'No fee')],
            ['Comment', detail.backofficeComment || '—'],
          ].map(([label, value]) => <div key={label} className="rounded-lg border border-gray-200 p-3"><p className="text-xs text-gray-500">{label}</p><p className="mt-1 break-words text-gray-900">{value}</p></div>)}
          <div className="rounded-lg border border-gray-200 p-3 sm:col-span-2">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs text-gray-500">Documents</p>
              {canManage && <label className="cursor-pointer rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white">Upload<input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(event) => void uploadDocument(event.target.files?.[0])} /></label>}
            </div>
            {documents.length === 0 ? <p className="text-sm text-gray-500">No documents attached</p> : <div className="space-y-1">{documents.map((document) => <button key={document.id} type="button" onClick={() => void downloadDocument(document)} className="block text-left text-sm text-indigo-600 hover:underline">{document.fileName}</button>)}</div>}
          </div>
        </div>
          );
        })()}
      </Modal>
    </div>
  );
}
